// === MODIFICATION: Added useMemo to the import list ===
import React, { useState, useEffect, useMemo } from "react";
// === END MODIFICATION ===
 import { useParams, useNavigate } from "react-router-dom";
 import {
  Box,
  Typography,
  FormControl,
  TextField, // Keep TextField for qualitative
  Button,
  FormLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid,
  styled,
  Slider, // Import Slider
  Input, // Import Input for slider value display/entry
  CircularProgress,
 } from "@mui/material";
 import config from './config.js';
 import { isTokenExpired } from './auth';

// Styled Select component for smaller width
const StyledSelect = styled(Select)({
 width: "100%", // Make select full width within its grid item
});

// Styled FormLabel for question text
 const StyledFormLabel = styled(FormLabel)({
  textAlign: "left",
  fontWeight: "normal",
  fontSize: "1.2rem",
  display: 'block', // Ensure it takes full width
  marginBottom: '0.5rem', // Add some space below label
 });

 // Styled Typography for larger helper text
 const StyledHelperText = styled(Typography)({
  fontSize: "1.2rem", // Adjust size as needed
  marginBottom: "1rem",
  marginTop: "3rem",
  fontWeight: "bold", // Make label text bold
 });

 // Styled Input for slider value display
 const StyledInput = styled(Input)({
    width: '50px', // Adjust width as needed
 });

 function SurveyQuestions() {
  const { surveyId } = useParams();
  const [questions, setQuestions] = useState([]);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [currentUserId, setCurrentUserId] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [questionOptions, setQuestionOptions] = useState({});
  const [formValidity, setFormValidity] = useState({});

  // --- Fetching Logic ---
  useEffect(() => {
    // Authentication check
    if (isTokenExpired()) {
        localStorage.removeItem('token');
        localStorage.removeItem('userRole');
        localStorage.removeItem('userName');
        localStorage.removeItem('userId');
        navigate('/login');
        return;
    }

    const fetchCurrentUserId = () => {
        // Use 'userId' key consistent with App.js fix
        const userId = localStorage.getItem('userId');
        if (userId) {
            setCurrentUserId(parseInt(userId, 10));
        } else {
            console.error("User ID not found in localStorage");
            navigate('/login');
        }
    };

    const fetchSurveyData = async () => {
        try {
            setLoading(true);
            setError(null);
            const token = localStorage.getItem("token");

            if (!surveyId) throw new Error("Survey ID is missing!");
            if (!token) throw new Error("Authentication token is missing!");

            // Fetch Questions
            console.log(`Fetching questions from: ${config.backendUrl}/survey_rounds/${surveyId}/questions`);
            const questionsResponse = await fetch(
                `${config.backendUrl}/survey_rounds/${surveyId}/questions`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!questionsResponse.ok) throw new Error(`Failed to fetch survey questions: ${questionsResponse.status}`);
            const questionsData = await questionsResponse.json();
            console.log("Fetched questions:", questionsData);
            setQuestions(questionsData);

            // Fetch Team Members
            console.log(`Fetching team members from: ${config.backendUrl}/api/team-members`);
            const teamMembersResponse = await fetch(
                `${config.backendUrl}/api/team-members`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!teamMembersResponse.ok) throw new Error(`Failed to fetch team members: ${teamMembersResponse.status}`);
            const teamMembersData = await teamMembersResponse.json();
            console.log("Fetched team members:", teamMembersData);
            setTeamMembers(teamMembersData);

            // Fetch Options for Multiple Choice Questions
            const mcQuestions = questionsData.filter(q => q[2] === "multiple_choice");
            console.log("Multiple choice questions found:", mcQuestions);
            const optionPromises = mcQuestions.map(q => fetchOptions(q[0], token));
            await Promise.all(optionPromises);

        } catch (error) {
            console.error("Error fetching survey data:", error);
            setError(error.message);
            if (error.message.includes("401")) {
                navigate('/login');
            }
        } finally {
            setLoading(false);
        }
    };

    // Helper to fetch options for a single question
    const fetchOptions = async (questionId, token) => {
        try {
            console.log(`Fetching options for question ${questionId} from: ${config.backendUrl}/question_options/${questionId}`);
            const response = await fetch(
                `${config.backendUrl}/question_options/${questionId}`,
                 { headers: { Authorization: `Bearer ${token}` } } // Assuming auth might be needed eventually
            );
            if (!response.ok) {
                console.error(`Fetch failed for options QID ${questionId}: Status ${response.status}, StatusText ${response.statusText}`);
                const errorBody = await response.text();
                console.error(`Error body: ${errorBody}`);
                // Check if the error is CORS-related (though backend fix should prevent this)
                if (response.type === 'opaque' || response.status === 0) {
                     throw new Error(`Failed to fetch options for question ${questionId} due to potential CORS issue or network error.`);
                } else {
                    throw new Error(`Failed to fetch options for question ${questionId}: ${response.status}`);
                }
            }
            const optionsData = await response.json();
            console.log(`Fetched options for question ${questionId}:`, optionsData);
            setQuestionOptions((prevOptions) => ({
                ...prevOptions,
                [questionId]: optionsData,
            }));
        } catch (error) {
            console.error(`Error in fetchOptions for question ${questionId}:`, error);
            // Update state to show error for specific question options
             setQuestionOptions((prevOptions) => ({
                ...prevOptions,
                [questionId]: [{ option_id: 'error', option_text: 'Error loading options' }], // Indicate error
            }));
        }
    };

    fetchCurrentUserId();
    fetchSurveyData();

  }, [surveyId, navigate]);

  // --- Initialize Form Validity ---
  useEffect(() => {
    const initialValidity = {};
    teamMembers.forEach(member => {
        initialValidity[member.user_id] = {};
        questions.forEach(question => {
            // Initialize all questions as invalid initially, except sliders
            initialValidity[member.user_id][question[0]] = false;
             if (question[2] === 'rating_0_4' || question[2] === 'rating_0_100') {
                 // Sliders are valid by default as they have a default value
                 initialValidity[member.user_id][question[0]] = true;
             }
        });
    });
    console.log("Initialized form validity:", initialValidity);
    setFormValidity(initialValidity);
  }, [questions, teamMembers]); // Re-run if questions or team members change

  // --- Handle Response Change ---
  const handleResponseChange = (questionId, value, rateeId) => {
    // Treat empty string for non-slider inputs as invalid for validity check
    const processedValue = value;
    console.log(`Response change: QID=${questionId}, RateeID=${rateeId}, Value=${processedValue}`);

    setResponses((prevResponses) => ({
        ...prevResponses,
        [rateeId]: {
            ...prevResponses[rateeId],
            [questionId]: processedValue,
        },
    }));

    // Update form validity - consider empty string as invalid for non-sliders
    // Sliders always have a numeric value, so they are valid if not null/undefined
    const isValid = processedValue !== undefined && processedValue !== null && processedValue !== '';
    console.log(`Validity check: QID=${questionId}, RateeID=${rateeId}, IsValid=${isValid}`);
    setFormValidity(prevValidity => {
        const rateeValidity = prevValidity[rateeId] || {};
        return {
            ...prevValidity,
            [rateeId]: {
                ...rateeValidity,
                [questionId]: isValid,
            }
        };
    });
  };

  // --- Handle Slider Change ---
  const handleSliderChange = (event, newValue, questionId, rateeId) => {
    // Sliders always provide a number, so just update
    handleResponseChange(questionId, newValue, rateeId);
  };

  // --- Handle Input Change (for Slider) ---
  const handleInputChange = (event, questionId, rateeId, min, max) => {
    let value = event.target.value === '' ? '' : Number(event.target.value);
     if (typeof value === 'number') {
        if (value < min) value = min;
        else if (value > max) value = max;
    }
    // Update response, validity will be handled by handleResponseChange
    handleResponseChange(questionId, value, rateeId);
  };

   // --- Handle Input Blur (for Slider) ---
   // Ensure a valid number is set if input is cleared or invalid
   const handleInputBlur = (questionId, rateeId, min, defaultValue) => {
    const currentValue = responses[rateeId]?.[questionId];
    if (currentValue === '' || currentValue === undefined || currentValue === null || (typeof currentValue === 'number' && currentValue < min)) {
      // Reset to default value if empty or below min on blur
      handleResponseChange(questionId, defaultValue, rateeId);
    }
  };

  // === MOVED useMemo CALL HERE ===
  // Calculate overall form validity for the submit button
   const isOverallFormValid = useMemo(() => {
        // Check only non-slider questions for explicit validity state true
        return teamMembers.every(member =>
            questions.every(question => {
                const questionType = question[2];
                if (questionType === 'rating_0_4' || questionType === 'rating_0_100') {
                    return true; // Sliders are considered valid by default
                }
                // For other types, check the validity state
                return formValidity[member.user_id]?.[question[0]] === true;
            })
        );
    }, [formValidity, questions, teamMembers]);
   console.log("Is Overall Form Valid (for submit button):", isOverallFormValid);
  // === END MOVED useMemo CALL ===


  // --- Form Submission Logic ---
  const handleSubmit = async (event) => {
      event.preventDefault();
      console.log("Form submission started");
      console.log("Current form validity state:", formValidity);
      console.log("Current responses state:", responses);

      // Ensure all required fields are valid
      // Use the memoized value directly
      if (!isOverallFormValid) {
          console.error("Form is not fully valid. Aborting submission.");
          alert("Please ensure all text fields and dropdown selections are completed for all team members before submitting.");
          return;
      }

      setLoading(true);
      try {
          const token = localStorage.getItem("token");
          if (!token) throw new Error("Authentication token missing.");

          // Prepare response data, ensuring sliders have default values if not touched
          const responseData = teamMembers.flatMap(member =>
              questions.map(question => {
                  const questionId = question[0];
                  const questionType = question[2];
                  let responseValue = responses[member.user_id]?.[questionId];

                  // Assign default values for sliders if they haven't been changed
                  if (responseValue === undefined || responseValue === null) {
                      if (questionType === 'rating_0_4') {
                          responseValue = 3; // Default for 0-4 slider
                      } else if (questionType === 'rating_0_100') {
                          responseValue = 80; // Default for 0-100 slider
                      }
                  }

                  return {
                      survey_id: parseInt(surveyId, 10),
                      question_id: parseInt(questionId, 10),
                      rater_id: currentUserId,
                      ratee_id: parseInt(member.user_id, 10),
                      response_value: String(responseValue), // Ensure string format
                  };
              })
          );


          console.log("Final request payload:", JSON.stringify({ survey_id: parseInt(surveyId, 10), responses: responseData }, null, 2));

          const response = await fetch(`${config.backendUrl}/survey_responses`, {
              method: "POST",
              headers: {
                  "Content-Type": "application/json",
                  "Authorization": `Bearer ${token}`,
              },
              body: JSON.stringify({ survey_id: parseInt(surveyId, 10), responses: responseData }),
          });

          const responseBody = await response.text();
          console.log("Server response status:", response.status);
          console.log("Server response body:", responseBody);

          if (response.ok) {
              console.log("All responses submitted successfully!");
              navigate('/dashboard'); // Navigate on success
              alert("Survey responses submitted successfully!"); // Provide user feedback
          } else {
              let errorMsg = `Failed to submit responses. Status: ${response.status}`;
              try {
                  const errorJson = JSON.parse(responseBody);
                  errorMsg = errorJson.message || errorJson.error || errorMsg;
              } catch (parseError) { /* Ignore */ }
              console.error("Failed to submit responses:", errorMsg);
              setError(errorMsg);
              alert(`Submission failed: ${errorMsg}`);
          }
      } catch (error) {
          console.error("Error submitting responses:", error);
          setError(`Error submitting responses: ${error.message}`);
          alert(`An error occurred: ${error.message}`);
      } finally {
          setLoading(false);
      }
  };

  // --- Render Logic ---
  if (loading && questions.length === 0) { // Show loading only on initial load
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>;
  }

  if (error) {
    return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="error" variant="h6">Failed to load survey</Typography>
            <Typography color="error">Error: {error}</Typography>
            <Button variant="contained" onClick={() => window.location.reload()} sx={{ mt: 2 }}>
                Retry
            </Button>
        </Box>
    );
  }

  const rating_0_4_questions = questions.filter(q => q[2] === "rating_0_4");
  const rating_0_100_questions = questions.filter(q => q[2] === "rating_0_100");
  const multipleChoiceQuestions = questions.filter(q => q[2] === "multiple_choice");
  const qualitativeQuestions = questions.filter(q => q[2] === "qualitative");


  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ textAlign: 'center', mb: 3 }}>
            360° Evaluation
        </Typography>
        <form onSubmit={handleSubmit}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ borderBottom: '1px solid #eee', pb: 1, mb: 2 }}>
              Please answer the questions on this page about every member of your team.  Questions are grouped by team member; you will answer a full series of questions about one team member before moving on to answer the same set of questions about the next team member.
          </Typography>
            {teamMembers.map((member) => (
                <Card key={member.user_id} sx={{ mb: 3, boxShadow: 3 }}>
                    <CardContent>
                        <Typography variant="h5" component="h2" gutterBottom sx={{ borderBottom: '1px solid #eee', pb: 1, mb: 2 }}>
                            Evaluating: {member.name}
                        </Typography>

                        {/* --- Rating 0-4 Questions (Slider) --- */}
                        {rating_0_4_questions.length > 0 && (
                            <Box sx={{ mb: 4 }}>
                                <StyledHelperText variant="body1">
                                    For this set of questions, please rate this team member on a scale of 0 to 4 (0=Never, 4=Always).
                                </StyledHelperText>
                                {rating_0_4_questions.map((question) => {
                                    const questionId = question[0];
                                    const rateeId = member.user_id;
                                    const defaultValue = 3; // Default value for this slider type
                                    const currentValue = responses[rateeId]?.[questionId] ?? defaultValue;

                                    return (
                                    <Box key={questionId} sx={{ mb: 3, p: 2, px: { xs: 2, sm: 3 }, border: '1px solid #e0e0e0', borderRadius: '4px' }}> {/* Adjusted padding */}
                                        <StyledFormLabel component="legend">{question[1]}</StyledFormLabel>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={12} sm={8} md={9}>
                                                <Slider
                                                    value={typeof currentValue === 'number' ? currentValue : defaultValue}
                                                    onChange={(event, newValue) => handleSliderChange(event, newValue, questionId, rateeId)}
                                                    aria-labelledby={`input-slider-${rateeId}-${questionId}`}
                                                    valueLabelDisplay="auto"
                                                    step={1}
                                                    marks={[ {value: 0, label: 'Never'}, {value: 1, label: 'Rarely'}, {value: 2, label: 'Sometimes'}, {value: 3, label: 'Often'}, {value: 4, label: 'Always'} ]}
                                                    min={0}
                                                    max={4}
                                                    name={`slider-${rateeId}-${questionId}`}
                                                    // === ADDED SX PROP FOR THICKNESS ===
                                                    sx={{
                                                        '& .MuiSlider-rail': { height: '6px', borderRadius: '3px', opacity: 0.3 }, // Thicker rail
                                                        '& .MuiSlider-track': { height: '8px', borderRadius: '4px' }, // Thicker track
                                                        '& .MuiSlider-thumb': { height: 25, width: 25 }, // Larger thumb
                                                        // Optional: Adjust mark size/position if needed
                                                        '& .MuiSlider-mark': { height: '10px', width: '2px', marginTop: '-4px'},
                                                        '& .MuiSlider-markLabel': { /* Adjust label style if needed */ }
                                                    }}
                                                    // === END SX PROP ===
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4} md={3}>
                                                <StyledInput
                                                    value={currentValue}
                                                    size="small"
                                                    onChange={(event) => handleInputChange(event, questionId, rateeId, 0, 4)}
                                                    onBlur={() => handleInputBlur(questionId, rateeId, 0, defaultValue)}
                                                    inputProps={{
                                                        step: 1, min: 0, max: 4, type: 'number',
                                                        'aria-labelledby': `input-slider-${rateeId}-${questionId}`,
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                    );
                                })}
                            </Box>
                        )}

                        {/* --- Rating 0-100 Questions (Slider) --- */}
                        {rating_0_100_questions.length > 0 && (
                            <Box sx={{ mb: 4 }}>
                                <StyledHelperText variant="body1">
                                    For this set of questions, please rate your teammate on how well they met your expectations (&lt;60=Does Not Meet, 80=Meets, 100=Exceeds).
                                </StyledHelperText>
                                {rating_0_100_questions.map((question) => {
                                    const questionId = question[0];
                                    const rateeId = member.user_id;
                                    const defaultValue = 80; // Default value for this slider type
                                    const currentValue = responses[rateeId]?.[questionId] ?? defaultValue;

                                    return (
                                     <Box key={questionId} sx={{ mb: 3, p: 2, px: { xs: 2, sm: 3 }, border: '1px solid #e0e0e0', borderRadius: '4px' }}> {/* Adjusted padding */}
                                        <StyledFormLabel component="legend">{question[1]}</StyledFormLabel>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={12} sm={8} md={9}>
                                                <Slider
                                                    value={typeof currentValue === 'number' ? currentValue : defaultValue}
                                                    onChange={(event, newValue) => handleSliderChange(event, newValue, questionId, rateeId)}
                                                    aria-labelledby={`input-slider-${rateeId}-${questionId}`}
                                                    valueLabelDisplay="auto"
                                                    step={1}
                                                    marks={[ {value: 50, label: 'Does Not Meet Expectations'}, {value: 80, label: 'Meets Expectations'}, {value: 100, label: 'Exceeds Expectations'} ]}
                                                    min={0}
                                                    max={100}
                                                    name={`slider-${rateeId}-${questionId}`}
                                                     // === ADDED SX PROP FOR THICKNESS ===
                                                     sx={{
                                                        '& .MuiSlider-rail': { height: '6px', borderRadius: '3px', opacity: 0.3 }, // Thicker rail
                                                        '& .MuiSlider-track': { height: '8px', borderRadius: '4px' }, // Thicker track
                                                        '& .MuiSlider-thumb': { height: 25, width: 25 }, // Larger thumb
                                                        // Optional: Adjust mark size/position if needed
                                                        '& .MuiSlider-mark': { height: '10px', width: '2px', marginTop: '-4px'},
                                                        '& .MuiSlider-markLabel': { /* Adjust label style if needed */ }
                                                    }}
                                                    // === END SX PROP ===
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4} md={3}>
                                                <StyledInput
                                                    value={currentValue}
                                                    size="small"
                                                    onChange={(event) => handleInputChange(event, questionId, rateeId, 0, 100)}
                                                    onBlur={() => handleInputBlur(questionId, rateeId, 0, defaultValue)}
                                                    inputProps={{
                                                        step: 1, min: 0, max: 100, type: 'number',
                                                        'aria-labelledby': `input-slider-${rateeId}-${questionId}`,
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                    );
                                })}
                            </Box>
                        )}

                        {/* --- Other Question Types (Multiple Choice & Qualitative) --- */}
                        {(multipleChoiceQuestions.length > 0 || qualitativeQuestions.length > 0) && (
                            <Box sx={{ mt: 3 }}>
                                <StyledHelperText variant="body1">
                                    Please provide feedback as requested below.
                                </StyledHelperText>

                                {/* Multiple Choice Questions */}
                                {multipleChoiceQuestions.map((question) => (
                                    <Box key={question[0]} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                                        <StyledFormLabel component="legend">{question[1]}</StyledFormLabel>
                                        <FormControl fullWidth error={!formValidity[member.user_id]?.[question[0]] && responses[member.user_id]?.[question[0]] !== undefined}>
                                            <StyledSelect
                                                value={responses[member.user_id]?.[question[0]] || ""}
                                                onChange={(event) =>
                                                    handleResponseChange( question[0], event.target.value, member.user_id )
                                                }
                                                displayEmpty
                                                required // HTML5 required
                                                name={`select-${member.user_id}-${question[0]}`}
                                            >
                                                <MenuItem value="" disabled> <em>Select an option...</em> </MenuItem>
                                                {(questionOptions[question[0]] || []).map((option) => (
                                                     // Check if it's the error indicator
                                                    option.option_id === 'error' ?
                                                        <MenuItem key={option.option_id} value="" disabled><em>{option.option_text}</em></MenuItem> :
                                                        <MenuItem key={option.option_id} value={option.option_text}>
                                                            {option.option_text}
                                                        </MenuItem>
                                                ))}
                                            </StyledSelect>
                                        </FormControl>
                                    </Box>
                                ))}

                                {/* Qualitative Questions */}
                                {qualitativeQuestions.map((question) => (
                                    <Box key={question[0]} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                                        <StyledFormLabel component="legend">{question[1]}</StyledFormLabel>
                                        <TextField
                                            multiline
                                            rows={4}
                                            value={responses[member.user_id]?.[question[0]] || ""}
                                            onChange={(event) =>
                                                handleResponseChange( question[0], event.target.value, member.user_id )
                                            }
                                            placeholder="Enter your feedback here"
                                            variant="outlined"
                                            fullWidth
                                            required // HTML5 required
                                            error={!formValidity[member.user_id]?.[question[0]] && responses[member.user_id]?.[question[0]] !== undefined} // Show error state if invalid
                                            name={`text-${member.user_id}-${question[0]}`}
                                        />
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </CardContent>
                </Card>
            ))}
            <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={!isOverallFormValid || loading} // Disable based on validity and loading state
                >
                    {loading ? <CircularProgress size={24} /> : "Submit All Responses"}
                </Button>
            </Box>
        </form>
    </Box>
  );
 }

 export default SurveyQuestions;
